import {
  Button,
  ButtonList,
  FlexBox,
  Icon,
  IconButton,
} from '@dixa/lib-shared-habla';
import * as Popover from '@radix-ui/react-popover';
import type {
  ReadSearchApiArg,
  SearchFilterCondition,
  SearchFilterConditionForField,
  SearchFilterField,
  SearchSortField,
} from 'lib-search-types';
import {
  filterFieldCatalogue,
  filterOperatorCatalogue,
  SearchFilter,
  type SearchFilterChangeEvent,
  type SearchFilterItem,
} from 'lib-search-ui';
import type { SortBy } from 'lib-shared-legacy-types';
import { useState } from 'react';

import { filterItems } from 'app/views/layout/views/search/components/filters/utils/definitions';
import { sortOptions } from 'app/views/layout/views/search/utils/sort/sortOptions';
import { SearchScopeFilter } from 'components/search';

import styles from './conversationScopeListToolbar.module.scss';

type ConversationScopeListToolbarProps = {
  /** The applied filter conditions. */
  readonly filters: readonly SearchFilterCondition<'conversations'>[];
  /** The applied sort. */
  readonly sort: SortBy<SearchSortField<'conversations'>> | '';
  /** Called when a filter changes. */
  readonly onFilterChange: (
    field: SearchFilterField<'conversations'>,
    event: SearchFilterChangeEvent,
  ) => void;
  /** Called when the sort changes. */
  readonly onSortChange: (
    sort: SortBy<SearchSortField<'conversations'>> | '',
  ) => void;
};

const statusItems: readonly SearchFilterItem[] = filterItems.status.map(
  ({ id, name }) => ({ value: `${id}`, label: `${name}` }),
);

const directionItems: readonly SearchFilterItem[] =
  filterItems.initial_direction.map(({ id, name }) => ({
    value: `${id}`,
    label: `${name}`,
  }));

const agentInitialArg: ReadSearchApiArg<'users'> = {
  scope: 'users',
  sortBy: { field: 'display_name', order: 'asc' },
  filters: {
    strategy: 'all',
    filters: [
      {
        field: 'user_type',
        operator: 'is_one',
        values: [{ value: 'member' }],
      },
    ],
  },
};

const contactInitialArg: ReadSearchApiArg<'users'> = {
  scope: 'users',
  sortBy: { field: 'display_name', order: 'asc' },
  filters: {
    strategy: 'all',
    filters: [
      {
        field: 'user_type',
        operator: 'is_one',
        values: [{ value: 'contact' }],
      },
    ],
  },
};

const queueInitialArg: ReadSearchApiArg<'queues'> = {
  scope: 'queues',
  filters: {
    strategy: 'all',
    filters: [
      {
        field: 'is_archived',
        operator: 'not_equals',
        values: [{ value: 'true' }],
      },
    ],
  },
  sortBy: { field: 'name', order: 'asc' },
};

const tagInitialArg: ReadSearchApiArg<'tags'> = {
  scope: 'tags',
  sortBy: { field: 'name', order: 'asc' },
};

/** The filters that are always visible. */
const primaryFilterFields = [
  'queue_id',
  'requested_by',
  'agent_id',
] as const satisfies readonly SearchFilterField<'conversations'>[];

/** The filters that are hidden until added from the add filter popover. */
const additionalFilterFields = [
  'status',
  'initial_channel',
  'initial_direction',
  'tags',
] as const satisfies readonly SearchFilterField<'conversations'>[];

type ToolbarFilterField =
  | (typeof primaryFilterFields)[number]
  | (typeof additionalFilterFields)[number];

type AdditionalFilterField = (typeof additionalFilterFields)[number];

const findCondition = <TField extends SearchFilterField<'conversations'>>(
  filters: readonly SearchFilterCondition<'conversations'>[],
  field: TField,
) =>
  filters.find(
    (
      condition,
    ): condition is SearchFilterConditionForField<'conversations', TField> =>
      condition.field === field,
  );

/** The basic filters and sorting of the conversation search. */
export const ConversationScopeListToolbar = ({
  filters,
  sort,
  onFilterChange,
  onSortChange,
}: ConversationScopeListToolbarProps): JSX.Element => {
  const [addedFields, setAddedFields] = useState<
    readonly AdditionalFilterField[]
  >([]);
  const [isAddFilterOpen, setIsAddFilterOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);

  // Applied filters stay visible even if the toolbar remounted and forgot they were added.
  const visibleAdditionalFields = additionalFilterFields.filter(
    (field) => addedFields.includes(field) || findCondition(filters, field),
  );
  const hiddenAdditionalFields = additionalFilterFields.filter(
    (field) => !visibleAdditionalFields.includes(field),
  );

  const renderFilter = (field: ToolbarFilterField) => {
    const condition = findCondition(filters, field);
    const { label } = filterFieldCatalogue.conversations[field];
    const { defaultOperator } = filterOperatorCatalogue.conversations[field];
    const handleChange = (event: SearchFilterChangeEvent) =>
      onFilterChange(field, event);

    switch (field) {
      case 'queue_id':
        return (
          <SearchScopeFilter
            key={field}
            scope="queues"
            placeholder="Search queues..."
            defaultOperator={defaultOperator}
            selectedValues={condition?.values}
            initialArg={queueInitialArg}
            onChange={handleChange}
          >
            {label}
          </SearchScopeFilter>
        );

      case 'requested_by':
        return (
          <SearchScopeFilter
            key={field}
            scope="users"
            placeholder="Search contacts..."
            defaultOperator={defaultOperator}
            selectedValues={condition?.values}
            initialArg={contactInitialArg}
            onChange={handleChange}
          >
            {label}
          </SearchScopeFilter>
        );

      case 'agent_id':
        return (
          <SearchScopeFilter
            key={field}
            scope="users"
            placeholder="Search agents..."
            defaultOperator={defaultOperator}
            selectedValues={condition?.values}
            initialArg={agentInitialArg}
            onChange={handleChange}
          >
            {label}
          </SearchScopeFilter>
        );

      case 'status':
        return (
          <SearchFilter
            key={field}
            items={statusItems}
            defaultOperator={defaultOperator}
            selectedValues={condition?.values}
            onChange={handleChange}
            showSearch={false}
          >
            {label}
          </SearchFilter>
        );

      case 'initial_channel':
        return (
          <SearchScopeFilter
            key={field}
            scope="channels"
            placeholder="Search channels..."
            defaultOperator={defaultOperator}
            selectedValues={condition?.values}
            onChange={handleChange}
          >
            {label}
          </SearchScopeFilter>
        );

      case 'initial_direction':
        return (
          <SearchFilter
            key={field}
            items={directionItems}
            defaultOperator={defaultOperator}
            selectedValues={condition?.values}
            onChange={handleChange}
            showSearch={false}
          >
            {label}
          </SearchFilter>
        );

      case 'tags':
        return (
          <SearchScopeFilter
            key={field}
            scope="tags"
            placeholder="Search tags..."
            defaultOperator={defaultOperator}
            selectedValues={condition?.values}
            initialArg={tagInitialArg}
            onChange={handleChange}
          >
            {label}
          </SearchScopeFilter>
        );
    }
  };

  const selectedSortOption =
    sortOptions.find(({ value }) => value === sort) ?? sortOptions[0];

  return (
    <FlexBox alignItems="center" justifyContent="space-between" padding="250">
      <FlexBox alignItems="center" flexWrap="wrap" gap="200 0">
        {primaryFilterFields.map(renderFilter)}

        {visibleAdditionalFields.map(renderFilter)}

        {hiddenAdditionalFields.length > 0 && (
          <Popover.Root
            open={isAddFilterOpen}
            onOpenChange={setIsAddFilterOpen}
          >
            <Popover.Trigger asChild>
              <IconButton
                icon="plus"
                variant="tertiary"
                aria-label="Add filter"
              />
            </Popover.Trigger>

            <Popover.Portal>
              <Popover.Content
                className={styles.popover}
                side="bottom"
                align="start"
                sideOffset={8}
                onOpenAutoFocus={(event) => event.preventDefault()}
              >
                <ButtonList>
                  {hiddenAdditionalFields.map((field) => (
                    <ButtonList.Item
                      key={field}
                      onClick={() => {
                        setIsAddFilterOpen(false);
                        setAddedFields((current) => [...current, field]);
                      }}
                    >
                      {filterFieldCatalogue.conversations[field].label}
                    </ButtonList.Item>
                  ))}
                </ButtonList>
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        )}
      </FlexBox>

      <Popover.Root open={isSortOpen} onOpenChange={setIsSortOpen}>
        <Popover.Trigger asChild>
          <Button
            variant="tertiary"
            slotEnd={<Icon name={isSortOpen ? 'angle-up' : 'angle-down'} />}
          >
            {selectedSortOption.label}
          </Button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            className={styles.popover}
            side="bottom"
            align="end"
            sideOffset={8}
            onOpenAutoFocus={(event) => event.preventDefault()}
          >
            <ButtonList>
              {sortOptions.map((option) => (
                <ButtonList.Item
                  key={option.value || 'relevance'}
                  isSelected={option === selectedSortOption}
                  onClick={() => {
                    setIsSortOpen(false);
                    onSortChange(option.value);
                  }}
                >
                  {option.label}
                </ButtonList.Item>
              ))}
            </ButtonList>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </FlexBox>
  );
};
